import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

export type TabNavType = 'searchParam' | 'state';

export interface UseTabsInput<T extends Tab> {
  tabs: T[];
  navType?: TabNavType;
  initialActiveTabId?: string;
}

export interface Tab extends Record<string, any> {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
}

export const TAB_PARAM_KEY = 'tab';

export const useTabs = <T extends Tab>({
  tabs,
  navType = 'state',
  initialActiveTabId,
}: UseTabsInput<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fallbackTab = tabs[0];
  const [stateActiveTabId, setStateActiveTabId] = useState(initialActiveTabId ?? fallbackTab?.id);

  const activeTabId = navType === 'state' ? stateActiveTabId : searchParams.get(TAB_PARAM_KEY);
  const activeTab: T | undefined = tabs.find((tab) => tab.id === activeTabId) ?? fallbackTab;

  const setActiveTab = ({ id }: { id: string }) => {
    if (navType === 'state') {
      setStateActiveTabId(id);
    } else {
      setSearchParams(
        (currentSearchParams) => ({
          ...Object.fromEntries(currentSearchParams),
          [TAB_PARAM_KEY]: id,
        }),
        {
          replace: true,
        },
      );
    }
  };

  useEffect(() => {
    if (!fallbackTab) return;
    if (!activeTabId || !tabs.find((tab) => tab.id === activeTabId)) {
      setActiveTab({ id: initialActiveTabId ?? fallbackTab.id });
    }
  }, [activeTabId, setActiveTab, tabs, initialActiveTabId, fallbackTab]);

  return {
    activeTab,
    setActiveTab,
  };
};
